import initializeSmimeStore from '../actions/initializeSmimeStore';
import SmimeExtensionCapabilities from '../store/schema/SmimeExtensionCapabilities';
import SmimeInstallationStatus from '../store/schema/SmimeInstallationStatus';
import SmimeResult from '../store/schema/SmimeResult';
import { getStore } from '../store/store';
import doesBrowserSupportModernExtension from '../utils/doesBrowserSupportModernExtension';
import SmimeErrorStateEnum from '../store/schema/SmimeErrorStateEnum';
import { trace } from 'owa-trace';
import { mutator } from 'satcheljs';
import {
    isSmimeNativeAppInstalledSeparately,
    isPluginUpToDate,
    isForceUpgrade,
} from '../utils/smimeUtils';

/**
 * Returns the installation status based on the inputs
 * @param getInstallationStatusRequest The wrapper object containing control status
 * @return SmimeInstallationStatus based on the input
 */
const getInstallationsStatus = (smimeResult: SmimeResult): SmimeInstallationStatus => {
    switch (smimeResult.ErrorCode) {
        case SmimeErrorStateEnum.NoError:
            // Set installationStatus based on the returned version
            const smimeExtensionCapabilities: SmimeExtensionCapabilities = smimeResult.Data as SmimeExtensionCapabilities;
            const version: string = smimeExtensionCapabilities.Version;
            return isPluginUpToDate(version)
                ? SmimeInstallationStatus.Installed
                : isForceUpgrade(version)
                ? SmimeInstallationStatus.NotUpToDateForce
                : SmimeInstallationStatus.NotUpToDateCompatible;
        case SmimeErrorStateEnum.ControlFailedToConnectToExtensionBackground:
            /**
             * This mode is likely to happen in Chrome when the companion app is not installed.
             * It means that the browser extension is present, but it could not communicate with the native app.
             */
            return SmimeInstallationStatus.MissingExtension;
        case SmimeErrorStateEnum.DomainRejectedError:
            // Set installationStatus to DomainNotAllowedExtensionOptionPage or DomainNotAllowedRestart
            return doesBrowserSupportModernExtension()
                ? SmimeInstallationStatus.DomainNotAllowedExtensionOptionPage
                : SmimeInstallationStatus.DomainNotAllowedRestart;
        default:
            // Set installationStatus to MissingExtension or MissingExtensionByAdmin
            return isSmimeNativeAppInstalledSeparately()
                ? SmimeInstallationStatus.MissingExtensionByAdmin
                : SmimeInstallationStatus.MissingExtension;
    }
};

export default mutator(initializeSmimeStore, actionMessage => {
    const smimeAdapterStore = getStore();
    const { smimeResult } = actionMessage;
    smimeAdapterStore.installationStatus = getInstallationsStatus(smimeResult);
    smimeAdapterStore.version = (smimeResult.Data as SmimeExtensionCapabilities).Version;
    trace.info(
        'S/MIME: Initialization complete. Initialization ErrorCode: ' + smimeResult.ErrorCode
    );
});
